import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, Lock } from "lucide-react";
import { z } from "zod";

const loginSchema = z.object({
  email: z.string().trim().email({ message: "Invalid email address" }).max(255),
  password: z.string().min(6, { message: "Password must be at least 6 characters" }),
});

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSignupMode, setIsSignupMode] = useState(false);
  const [isResetMode, setIsResetMode] = useState(false);
  const { signIn, signUp, isAdmin, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const getSafeNext = (): string | null => {
    const raw = searchParams.get("next");
    if (!raw) return null;
    if (!raw.startsWith("/") || raw.startsWith("//")) return null;
    return raw;
  };

  const goAfterAuth = () => {
    const next = getSafeNext();
    if (next) {
      window.location.href = next;
      return;
    }
    navigate("/admin/dashboard");
  };

  useEffect(() => {
    if (!authLoading && isAdmin) {
      goAfterAuth();
    }
  }, [isAdmin, authLoading, navigate]);

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const emailSchema = z.string().trim().email({ message: "Invalid email address" });
      const validatedEmail = emailSchema.parse(email);
      setIsLoading(true);

      const { supabase } = await import("@/integrations/supabase/client");
      const redirectUrl = `${window.location.origin}/admin/login`;
      
      const { error } = await supabase.auth.resetPasswordForEmail(validatedEmail, {
        redirectTo: redirectUrl,
      });

      if (error) {
        toast.error(error.message || "Failed to send reset email");
        return;
      }

      toast.success("Password reset email sent! Check your inbox.");
      setIsResetMode(false);
      setEmail("");
    } catch (error) {
      if (error instanceof z.ZodError) {
        error.errors.forEach((err) => {
          toast.error(err.message);
        });
      } else {
        toast.error("An error occurred");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (isSignupMode) {
        if (password !== confirmPassword) {
          toast.error("Passwords do not match");
          return;
        }
        const validated = loginSchema.parse({ email, password });
        setIsLoading(true);

        const { error } = await signUp(validated.email, validated.password);

        if (error) {
          toast.error(error.message || "Failed to create account");
          return;
        }

        // After successful signup, assign admin role
        const { supabase } = await import("@/integrations/supabase/client");
        const { data: { user } } = await supabase.auth.getUser();
        
        if (user) {
          const { error: roleError } = await supabase
            .from('user_roles')
            .insert({ user_id: user.id, role: 'admin' });

          if (roleError) {
            // Don't log error details to console to prevent information leakage
            // If an admin already exists, close registrations and sign the user out
            await supabase.auth.signOut().catch(() => {});
            toast.error("Registration is closed. An admin already exists.");
            return;
          }
        }

        toast.success("Admin account created successfully!");
        goAfterAuth();
      } else {
        const validated = loginSchema.parse({ email, password });
        setIsLoading(true);

        const { error } = await signIn(validated.email, validated.password);

        if (error) {
          toast.error(error.message || "Failed to sign in");
          return;
        }

        toast.success("Signed in successfully!");
        goAfterAuth();
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        error.errors.forEach((err) => {
          toast.error(err.message);
        });
      } else {
        toast.error("An error occurred");
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-secondary/20 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 text-center">
          <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Lock className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="text-2xl font-bold">
            {isResetMode ? "Reset Password" : isSignupMode ? "Create Admin Account" : "Admin Login"}
          </CardTitle>
          <CardDescription>
            {isResetMode
              ? "Enter your email to receive a password reset link"
              : isSignupMode 
                ? "Register the first admin account"
                : "Sign in to access the blog management system"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={isResetMode ? handlePasswordReset : handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="admin@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>
            {!isResetMode && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>
                {isSignupMode && (
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      disabled={isLoading}
                    />
                  </div>
                )}
              </>
            )}
            <div className="space-y-2">
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {isResetMode ? "Sending email..." : isSignupMode ? "Creating account..." : "Signing in..."}
                  </>
                ) : (
                  isResetMode ? "Send Reset Email" : isSignupMode ? "Create Admin Account" : "Sign In"
                )}
              </Button>
              
              {!isResetMode && (
                <>
                  <Button 
                    variant="outline" 
                    className="w-full" 
                    type="button"
                    onClick={() => setIsSignupMode(!isSignupMode)}
                    disabled={isLoading}
                  >
                    {isSignupMode ? "Back to Login" : "Create First Admin Account"}
                  </Button>
                  
                  {!isSignupMode && (
                    <Button 
                      variant="ghost" 
                      className="w-full" 
                      type="button"
                      onClick={() => setIsResetMode(true)}
                      disabled={isLoading}
                    >
                      Forgot Password?
                    </Button>
                  )}
                </>
              )}
              
              {isResetMode && (
                <Button 
                  variant="outline" 
                  className="w-full" 
                  type="button"
                  onClick={() => {
                    setIsResetMode(false);
                    setEmail("");
                  }}
                  disabled={isLoading}
                >
                  Back to Login
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
